import React, { useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { Building2, ChevronDown, Check } from 'lucide-react';

export default function OrgSwitcher({ isExpanded }) {
  const { user, switchOrg, isSuperAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const memberships = user?.memberships || [];

  // Don't render if user has 0 or 1 org (super admins always see the switcher)
  if (memberships.length <= 1 && !isSuperAdmin) return null;

  const handleSwitch = async (orgId) => {
    if (orgId === user?.org_id || switching) return;
    setSwitching(true);
    try {
      await switchOrg(orgId);
      window.location.reload(); // Refresh data for new org
    } catch (err) {
      console.error('Failed to switch org:', err);
    } finally {
      setSwitching(false);
      setOpen(false);
    }
  };

  const currentOrg = memberships.find((m) => m.organisation_id === user?.org_id);
  const orgLabel = currentOrg?.name || 'Select Organisation';

  return (
    <div style={{ position: 'relative', padding: '0 10px', marginBottom: 8 }}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="sidebar-item"
        style={{ width: '100%', justifyContent: isExpanded ? 'flex-start' : 'center' }}
        title={!isExpanded ? orgLabel : undefined}
      >
        <span className="sidebar-icon">
          <Building2 size={18} />
        </span>
        {isExpanded && (
          <>
            <span
              className="sidebar-label"
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '12px',
              }}
            >
              {orgLabel}
            </span>
            <ChevronDown
              size={14}
              style={{
                transition: 'transform 0.2s',
                transform: open ? 'rotate(180deg)' : 'none',
                opacity: 0.5,
              }}
            />
          </>
        )}
      </button>

      {open && isExpanded && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 10,
            right: 10,
            zIndex: 50,
            /* --card-bg was never declared, so this fell back to the pre-Ledger navy
               in every theme. A dropdown is a solid panel: --surface-solid. */
            background: 'var(--surface-solid)',
            /* No fallback: --border is declared in :root, so the fallback was
               dead in every theme. Leaving a white literal in it only gave the
               parity checker something ambiguous to resolve. */
            border: '1px solid var(--border)',
            borderRadius: '8px',
            padding: '4px',
            marginTop: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}
        >
          {memberships.map((m) => (
            <button
              key={m.organisation_id}
              onClick={() => handleSwitch(m.organisation_id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '8px 10px',
                borderRadius: 6,
                border: 'none',
                background:
                  m.organisation_id === user?.org_id
                    ? 'rgba(var(--accent-rgb), 0.08)'
                    : 'transparent',
                color: 'var(--body)',
                fontSize: '13px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span style={{ flex: 1 }}>{m.name}</span>
              {m.organisation_id === user?.org_id && (
                <Check size={14} style={{ color: 'var(--accent)' }} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
